/**
 * Microsoft Clarity client API wrapper.
 *
 * WHY THIS MODULE EXISTS
 * Clarity's dashboard answers "what did visitors do"; it cannot answer "show me
 * the sessions where we lost money" unless the app tells it what happened. That
 * is what these APIs are for:
 *   - `set`      → custom tags, which become FILTER dimensions in the dashboard
 *   - `event`    → API events, which become Smart events (filters + funnels)
 *   - `upgrade`  → prioritise/retain THIS session for recording
 *   - `identify` → attach a non-PII id so a replay can be tied to an order
 *
 * DESIGN RULES (all load-bearing):
 *
 * 1. **Never throw, never block.** Every entry point is wrapped. Analytics is
 *    the least important thing on the page; a Clarity failure must not be able
 *    to break a checkout. Same posture as `lib/analytics.ts`.
 *
 * 2. **Tag KEYS are a closed vocabulary** (`ClarityTagKey`). A filter dimension
 *    is only useful if it is spelled identically on every call site — one
 *    `deliveryscope` typo silently creates a second, near-empty dimension in the
 *    dashboard while the real one looks incomplete. The union type makes that a
 *    compile error rather than a data-quality mystery.
 *
 * 3. **Tag VALUES are PII-screened at runtime, and that is a security control,
 *    not tidiness.** The keys are typed, but a value is an ordinary string, so
 *    the realistic accident is passing the right key with the wrong value —
 *    `claritySet("productSlug", order.customerName)`. `isPiiLikeValue` rejects
 *    anything email-, phone-, postcode- or free-text-shaped. It deliberately
 *    also treats LONG and MULTI-LINE values as PII-shaped: a gift message, an
 *    address line and a bereavement note are all just "long text", and no
 *    allow-list of field names can catch a value that arrived through the wrong
 *    variable. Cf. `trackChatLead`, where exactly this shape shipped the
 *    visitor's raw first 60 characters to GA4 and had to be typed out of
 *    existence.
 *
 * 4. **A dropped call is always preferable to a leaked one.** A rejected value
 *    is a no-op (plus a dev-only warning that never logs the value itself). We
 *    never "sanitise and send" — a truncated address is still an address.
 *
 * 5. **Calls made before the tag loads are queued, boundedly.**
 *    `components/analytics/MicrosoftClarity.tsx` uses `strategy="lazyOnload"`
 *    (deliberate — `afterInteractive` put script evaluation inside the
 *    INP-sensitive first-interaction window). Until that inline snippet runs,
 *    `window.clarity` does not exist and a bare call is LOST — which would drop
 *    exactly the early-session tags (`deliveryScope`, `cartValueBand`) that make
 *    a replay findable. The queue is capped and time-boxed so a visitor who
 *    declined analytics consent (Clarity therefore never loads at all) cannot
 *    accumulate an unbounded array for the life of the tab.
 *
 * Clarity is loaded ONLY after analytics consent (`GatedMicrosoftClarity`) and
 * never on `/admin` or `/florist` (`ClientProviders`), so nothing here needs its
 * own consent check — but nothing here may ASSUME the tag is present either.
 */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/* ------------------------------------------------------------------ */
/*  Vocabulary                                                         */
/* ------------------------------------------------------------------ */

/**
 * Every custom tag key the app may set. Adding one here is the whole cost of a
 * new filter dimension — Clarity imposes no limit on the number of tags.
 *
 * Keep these SHORT and non-identifying: a key is displayed verbatim in the
 * dashboard's filter list, which every project member can see.
 */
export const CLARITY_TAG_KEYS = [
  /** "leeds" | "uk" | "collection" — which fulfilment path the customer is on. */
  "deliveryScope",
  /** Coarse cart-value band. Deliberately banded, never the exact total. */
  "cartValueBand",
  /** Furthest checkout step reached ("1".."5"). */
  "checkoutStep",
  /** "paid" | "cancelled" | "unpaid-fallback" | "free" */
  "paymentOutcome",
  /** "in-range" | "out-of-range" | "lookup-failed" — Leeds postcode quote. */
  "quoteResult",
  /** "yes" | "no" — whether a promo code was applied. */
  "promoApplied",
  /** Product slug on a PDP. A slug is public catalogue data, not customer data. */
  "productSlug",
  /** "yes" | "no" — running as an installed PWA. */
  "pwaStandalone",
] as const;

export type ClarityTagKey = (typeof CLARITY_TAG_KEYS)[number];

/**
 * API event names. These surface in Clarity as Smart events, so they can drive
 * funnels and filters WITHOUT depending on a CSS selector — a selector-based
 * smart event silently dies on the next Tailwind refactor, which is the failure
 * mode this vocabulary exists to avoid.
 *
 * Names mirror the GA4 event names in `lib/analytics.ts` on purpose: two
 * analytics stacks describing the same funnel in different words is how the
 * numbers stop reconciling.
 */
export const CLARITY_EVENTS = [
  "add_to_cart",
  "begin_checkout",
  "purchase",
  "generate_lead",
  "checkout_failed",
  "payment_cancelled",
  "delivery_quote_failed",
] as const;

export type ClarityEvent = (typeof CLARITY_EVENTS)[number];

/**
 * Reasons to `upgrade` (prioritise + retain) a session. Each one is a state
 * where we will later WANT the tape and cannot get it retroactively.
 */
export const CLARITY_UPGRADE_REASONS = [
  /** A real client-side error was reported (post-triage, see error-triage.ts). */
  "js-error",
  /** Returned from Stripe with `?canceled=1`. */
  "payment-cancelled",
  /** `/api/checkout` refused or failed. */
  "checkout-failed",
  /** Order page reached in an unpaid state ("message us to pay"). */
  "order-unpaid",
  /** Postcode/pricing lookup failed, so the customer saw no price. */
  "delivery-quote-failed",
] as const;

export type ClarityUpgradeReason = (typeof CLARITY_UPGRADE_REASONS)[number];

/* ------------------------------------------------------------------ */
/*  PII screening                                                      */
/* ------------------------------------------------------------------ */

/**
 * Longest permitted tag/identifier value. Every legitimate value in
 * `CLARITY_TAG_KEYS` is a short enum member or a slug; anything longer is free
 * text, and free text on this site means a gift message, an address or a
 * bereavement note. The cap is the backstop that catches PII arriving through a
 * variable no name-based rule could anticipate.
 */
export const MAX_CLARITY_VALUE_LENGTH = 64;

/** An email address anywhere in the value. */
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/**
 * A UK postcode — full, with or without the space (`LS1 4DY`, `LS14DY`).
 * Deliberately loose: a false positive costs one dropped tag, a false negative
 * writes a customer's postcode into a third party.
 */
const UK_POSTCODE_RE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;

/**
 * A phone-number-shaped run of digits: 7+ digits ignoring spaces, dashes,
 * brackets and a leading `+`. Catches `07731…`, `+44 7731 …`, `0113 …`.
 */
const PHONE_RE = /(?:\+?\d[\s\-().]*){7,}/;

/** Newlines/tabs — a multi-line value is prose, i.e. free text. */
const MULTILINE_RE = /[\n\r\t]/;

/**
 * True when `value` looks like it could carry personal data and must therefore
 * never be sent.
 */
export function isPiiLikeValue(value: string): boolean {
  if (value.length > MAX_CLARITY_VALUE_LENGTH) return true;
  if (MULTILINE_RE.test(value)) return true;
  if (EMAIL_RE.test(value)) return true;
  if (UK_POSTCODE_RE.test(value)) return true;
  if (PHONE_RE.test(value)) return true;
  return false;
}

/** Dev-only diagnostic. Never logs the value — that would defeat the point. */
function warnRejected(what: string, key: string): void {
  if (process.env.NODE_ENV === "production") return;
  console.warn(
    `[clarity] refused to send ${what} "${key}": value looks like personal data ` +
      `(too long, multi-line, or email/phone/postcode-shaped). See lib/clarity.ts.`
  );
}

/* ------------------------------------------------------------------ */
/*  Transport (bounded pre-load queue)                                 */
/* ------------------------------------------------------------------ */

/**
 * Cap on calls buffered before the Clarity tag exists. 40 is far above what a
 * real page emits before `lazyOnload` fires (a handful of tags), and low enough
 * that a consent-declining visitor's tab holds a trivial array forever.
 */
const MAX_QUEUED_CALLS = 40;

/** Give up waiting for the tag after this long. Mirrors `whenGtagReady`. */
const READY_TIMEOUT_MS = 15_000;
const READY_POLL_MS = 250;

let queued: unknown[][] = [];
let poller: number | null = null;
let gaveUp = false;

function clarityFn(): ((...args: unknown[]) => void) | null {
  if (typeof window === "undefined") return null;
  return typeof window.clarity === "function" ? window.clarity : null;
}

function flush(fn: (...args: unknown[]) => void): void {
  const pending = queued;
  queued = [];
  for (const args of pending) {
    try {
      fn(...args);
    } catch {
      /* a single bad call must not strand the rest of the queue */
    }
  }
}

function startPoller(): void {
  if (poller !== null || gaveUp || typeof window === "undefined") return;
  const startedAt = Date.now();
  poller = window.setInterval(() => {
    const fn = clarityFn();
    if (fn) {
      if (poller !== null) window.clearInterval(poller);
      poller = null;
      flush(fn);
      return;
    }
    if (Date.now() - startedAt >= READY_TIMEOUT_MS) {
      // Consent declined, ad-blocked, or a staff route where Clarity is never
      // mounted. Drop the buffer and stop polling for the life of the page —
      // re-arming per call would poll forever on every storefront page.
      if (poller !== null) window.clearInterval(poller);
      poller = null;
      gaveUp = true;
      queued = [];
    }
  }, READY_POLL_MS);
}

/**
 * Hand a raw call to Clarity, queueing it if the tag hasn't loaded yet.
 * Exported for tests; call sites use the typed helpers below.
 */
export function clarityCall(...args: unknown[]): void {
  try {
    if (typeof window === "undefined") return;
    const fn = clarityFn();
    if (fn) {
      fn(...args);
      return;
    }
    if (gaveUp) return;
    if (queued.length >= MAX_QUEUED_CALLS) return;
    queued.push(args);
    startPoller();
  } catch {
    /* analytics must never surface an error to the page */
  }
}

/**
 * Stop recording the current session, immediately and for the life of this tab.
 *
 * WHY THIS IS NEEDED, when `ClientProviders` already refuses to render the tag
 * on `/admin` and `/florist`: that gate only decides whether the loader is
 * MOUNTED. Once the snippet has run on a storefront page it has installed a
 * global and a history hook that keep recording across client-side navigation,
 * and unmounting a React component cannot take either back. So a staff member
 * who browsed the shop and then clicked through to the dashboard was recorded
 * all the way in — which breaks the privacy promise `ClientProviders` states,
 * and is why `/admin`, `/admin/orders` and `/admin/analytics` appeared in the
 * customer behaviour data as 15 of one day's 60 recorded sessions.
 *
 * Deliberately NOT routed through `clarityCall`: a queued "stop" would sit in
 * the buffer and fire later against a session that legitimately started, which
 * is the opposite of the intent. If the tag isn't loaded there is nothing to
 * stop, and doing nothing is correct.
 */
export function clarityStop(): void {
  try {
    if (typeof window === "undefined") return;
    // Remember, for the rest of this tab's session, that a staff portal was
    // visited. Stopping the tag is not enough on its own: the owner then clicks
    // OUT of the dashboard to the shop, which begins a BRAND NEW Clarity
    // session that `clarityStop` never sees. See `isStaffOriginatedSession`.
    markStaffOriginatedSession();
    // Drop anything buffered before it can be flushed into the staff session.
    queued = [];
    clarityFn()?.("stop");
  } catch {
    /* analytics must never surface an error to the page */
  }
}

/* ------------------------------------------------------------------ */
/*  Staff-originated sessions (entry-referrer suppression)             */
/* ------------------------------------------------------------------ */

/**
 * Per-tab marker recording "this browsing session started in, or passed
 * through, a staff portal". `sessionStorage`, not `localStorage`: the claim is
 * about ONE browsing session, and a persistent flag would silently stop
 * recording a shared device forever.
 */
export const STAFF_SESSION_STORAGE_KEY = "fm-clarity-staff-session";

/** `/admin`, `/admin/orders`, `/florist`, `/florist/12` — never `/administrators`. */
const STAFF_PATH_RE = /^\/(?:admin|florist)(?:\/|$)/;

/**
 * True when `referrer` is a page of OUR OWN site inside a staff portal.
 *
 * FIRST-PARTY ONLY, and that is the security-relevant half. A referrer is
 * attacker-controllable: any site can link here from `https://evil.test/admin`,
 * and treating that as staff would let a third party switch our analytics off
 * for arbitrary visitors. The origin must match ours before the path is even
 * looked at. The referrer itself is never sent anywhere — it is read, reduced
 * to one boolean, and discarded.
 */
export function isFirstPartyStaffReferrer(
  referrer: string | null | undefined,
  siteOrigin: string | null | undefined
): boolean {
  const raw = (referrer ?? "").trim();
  const own = (siteOrigin ?? "").trim();
  if (!raw || !own) return false;
  try {
    const ref = new URL(raw);
    const site = new URL(own);
    if (ref.origin !== site.origin) return false;
    return STAFF_PATH_RE.test(ref.pathname);
  } catch {
    // A relative or unparseable referrer proves nothing about staff origin.
    return false;
  }
}

/** Set the per-tab marker. Storage can be disabled/full — never throw. */
function markStaffOriginatedSession(): void {
  try {
    window.sessionStorage?.setItem(STAFF_SESSION_STORAGE_KEY, "1");
  } catch {
    /* private mode / quota — the referrer check below still catches the entry */
  }
}

/**
 * True when Clarity must NOT be started for this session because it is the
 * owner's, not a customer's.
 *
 * WHY THE `clarityStop` GATE WAS ONLY HALF THE FIX. `clarityStop` handles
 * storefront → `/admin`. The measured leak runs the other way: on 2026-08-01,
 * **11 of 60 recorded sessions were referred from
 * `https://thefloralmuse.uk/admin/orders`** — the owner clicking out of the
 * dashboard into the shop. That click starts a FRESH Clarity session, on a page
 * where the tag is legitimately mounted, so nothing existing could see it. At
 * ≥25% of the dataset, every "customer" number on /admin/behaviour was
 * describing the owner.
 *
 * Two signals, either of which suppresses:
 *   1. the per-tab marker set by `clarityStop` (covers same-tab SPA exits, and
 *      makes the decision sticky for the rest of the session), and
 *   2. the ENTRY referrer of this document, when it is first-party staff.
 *
 * Signal 2 also latches the marker, so a further click deeper into the shop —
 * where `document.referrer` is a storefront page again — stays suppressed.
 *
 * Fails OPEN toward privacy: if this cannot decide, it returns false and the
 * visitor is recorded, which is the normal customer case.
 */
export function isStaffOriginatedSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage?.getItem(STAFF_SESSION_STORAGE_KEY) === "1") return true;
  } catch {
    /* storage unavailable — fall through to the referrer check */
  }
  const staff = isFirstPartyStaffReferrer(
    typeof document === "undefined" ? "" : document.referrer,
    window.location?.origin
  );
  if (staff) markStaffOriginatedSession();
  return staff;
}

/** Test seam: forget the per-tab staff marker. */
export function __resetStaffSessionForTests(): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage?.removeItem(STAFF_SESSION_STORAGE_KEY);
    }
  } catch {
    /* nothing to do */
  }
}

/** Test seam: forget any queued calls and re-arm the pre-load buffer. */
export function __resetClarityQueueForTests(): void {
  queued = [];
  gaveUp = false;
  if (poller !== null && typeof window !== "undefined") {
    window.clearInterval(poller);
  }
  poller = null;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Set a custom tag (a dashboard filter dimension).
 *
 * Rejects PII-shaped values outright — see rule 3 in the module header.
 */
export function claritySet(key: ClarityTagKey, value: string): void {
  if (!value) return;
  if (isPiiLikeValue(value)) {
    warnRejected("tag", key);
    return;
  }
  clarityCall("set", key, value);
}

/** Record an API event (becomes a Smart event usable in filters + funnels). */
export function clarityEvent(name: ClarityEvent): void {
  clarityCall("event", name);
}

/**
 * Prioritise this session for recording.
 *
 * Call at the moments we will later want the tape and can't get it
 * retroactively — recordings expire after 30 days, and masking/retention
 * decisions can never be applied backwards.
 */
export function clarityUpgrade(reason: ClarityUpgradeReason): void {
  clarityCall("upgrade", reason);
}

/**
 * Attach a non-PII identifier to the session so a replay can be found from an
 * order.
 *
 * `id` is an ORDER CODE or customer id — never an email or phone number, which
 * the PII screen enforces rather than trusting the caller. Treat the id as
 * NON-SECRET: it is sent to a third party and displayed in their UI, so it must
 * never be the only thing standing between a stranger and an order (the order
 * page's own access control is what does that job).
 */
export function clarityIdentify(id: string, friendlyName?: string): void {
  if (!id) return;
  if (isPiiLikeValue(id)) {
    warnRejected("identifier", "custom-id");
    return;
  }
  if (friendlyName && isPiiLikeValue(friendlyName)) {
    warnRejected("identifier", "friendly-name");
    return;
  }
  // Positional API: (custom-id, custom-session-id, custom-page-id, friendly-name).
  // Pass undefined for the two we don't use so `friendlyName` lands in the right
  // slot — Clarity has no keyword form.
  clarityCall("identify", id, undefined, undefined, friendlyName);
}

/* ------------------------------------------------------------------ */
/*  Derived tag values                                                 */
/* ------------------------------------------------------------------ */

/**
 * Band a cart total into a filterable bucket.
 *
 * BANDED, never exact: an exact per-session basket value is a high-cardinality
 * dimension that is useless as a filter, and combined with a replay's timestamp
 * it edges toward identifying a specific order inside a third-party tool. Bands
 * answer the question actually being asked ("do big baskets abandon more?").
 */
export function cartValueBand(totalPence: number): string {
  if (!Number.isFinite(totalPence) || totalPence <= 0) return "0";
  const pounds = totalPence / 100;
  if (pounds < 25) return "under-25";
  if (pounds < 50) return "25-50";
  if (pounds < 100) return "50-100";
  if (pounds < 200) return "100-200";
  return "200-plus";
}
